import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
  Row,
  Col,
  Input,
  Card,
  CardBody,
  Form,
  Button,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { fetchData, postData } from "../../services/apiServices";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const ActiveResolutionList = () => {
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [processingResolution, setProcessingResolution] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    resolutionId: null,
    action: null,
    resolutionInfo: null,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRows: 0,
  });

  // Fetch categories
  useEffect(() => {
    const getCategories = async () => {
      try {
        const response = await fetchData("api/admin/categories");
        console.log("Categories fetched:", response);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getCategories();
  }, []);

  // Fetch active resolution markets from the new API
  useEffect(() => {
    const getActiveResolutions = async () => {
      try {
        let url = `api/event/resolution/active?page=${pagination.page}&limit=${pagination.limit}`;

        if (searchText) {
          url += `&search=${encodeURIComponent(searchText)}`;
        }

        if (selectedCategory) {
          url += `&category=${selectedCategory}`;
        }

        console.log("Fetching active resolutions with URL:", url);
        const response = await fetchData(url);
        console.log("Active resolutions response:", response);

        if (response.success && response.markets) {
          setMarkets(response.markets);
          if (response.pagination) {
            setPagination({
              ...pagination,
              totalRows: response.pagination.total || response.markets.length,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching active resolutions:", error);
      } finally {
        setLoading(false);
      }
    };
    getActiveResolutions();
  }, [pagination.page, pagination.limit, searchText, selectedCategory]);

  // Handle resolution review (approve/reject)
  const handleResolutionReview = async (resolutionId, decision) => {
    try {
      setProcessingResolution(resolutionId);

      const reviewData = {
        resolution_id: resolutionId,
        decision: decision,
        review_notes:
          decision === "approved"
            ? "Evidence confirms the outcome is correct"
            : "Evidence does not sufficiently support the proposed outcome",
      };

      const response = await postData("resolution/review", reviewData);

      if (response.success) {
        toast.success(`Resolution ${decision} successfully`);

        // Refresh the data
        const updatedMarkets = [...markets];
        const marketIndex = updatedMarkets.findIndex(
          (market) =>
            market.latestResolution &&
            market.latestResolution._id === resolutionId
        );

        if (marketIndex !== -1) {
          // Update the status in the local state
          updatedMarkets[marketIndex].latestResolution.status = decision;
          setMarkets(updatedMarkets);
        } else {
          // If we can't find the exact resolution, just refresh the data
          refreshData();
        }
      } else {
        toast.error(
          `Failed to ${decision} resolution: ${
            response.message || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error(`Error ${decision} resolution:`, error);
      toast.error(
        `Failed to ${decision} resolution: ${error.message || "Unknown error"}`
      );
    } finally {
      setProcessingResolution(null);
      // Close the confirmation modal
      setConfirmationModal({
        isOpen: false,
        resolutionId: null,
        action: null,
        resolutionInfo: null,
      });
    }
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      let url = `resolution/active?page=${pagination.page}&limit=${pagination.limit}`;

      if (searchText) {
        url += `&search=${encodeURIComponent(searchText)}`;
      }

      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }

      const response = await fetchData(url);

      if (response.success && response.markets) {
        setMarkets(response.markets);
        if (response.pagination) {
          setPagination({
            ...pagination,
            totalRows: response.pagination.total || response.markets.length,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching active resolutions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle row click to navigate to resolution detail page
  const handleRowClick = (row) => {
    navigate(`/active-resolution/${row._id}`);
  };

  const handlePageChange = (page) => {
    setPagination({
      ...pagination,
      page,
    });
  };

  const handlePerRowsChange = (newLimit) => {
    setPagination({
      ...pagination,
      limit: newLimit,
    });
  };

  // Open confirmation modal
  const openConfirmationModal = (resolutionId, action, resolutionInfo) => {
    setConfirmationModal({
      isOpen: true,
      resolutionId,
      action,
      resolutionInfo,
    });
  };

  // Close confirmation modal
  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      resolutionId: null,
      action: null,
      resolutionInfo: null,
    });
  };

  // Confirm action
  const confirmAction = () => {
    const { resolutionId, action } = confirmationModal;
    if (resolutionId && action) {
      handleResolutionReview(resolutionId, action);
    }
  };

  // Helper function to get user-friendly status text
  const getResolutionStatusText = (resolutionState) => {
    switch (resolutionState) {
      case "resolution_proposed":
        return "Resolution Proposed";
      case "dispute_round_1":
        return "Disputed (Round 1)";
      case "dispute_round_1_resolved":
        return "Dispute Round 1 Resolved";
      case "dispute_round_2":
        return "Disputed (Round 2)";
      case "dispute_round_2_resolved":
        return "Dispute Round 2 Resolved";
      case "dispute_round_3":
        return "Disputed (Round 3)";
      case "dispute_round_3_resolved":
        return "Dispute Round 3 Resolved";
      case "finalized":
        return "Finalized";
      default:
        return resolutionState || "Unknown";
    }
  };

  // Helper function to get status badge color
  const getStatusBadgeColor = (resolutionState) => {
    if (resolutionState?.includes("dispute")) {
      return "bg-warning";
    } else if (resolutionState === "resolution_proposed") {
      return "bg-info";
    } else if (resolutionState === "finalized") {
      return "bg-success";
    } else {
      return "bg-secondary";
    }
  };

  // Calculate remaining time in dispute window
  const getDisputeWindowRemaining = (market) => {
    if (!market || !market.resolution_window || !market.updatedAt) {
      return null;
    }

    // Get resolution window in hours
    const resolutionWindowHours = market.resolution_window;

    // Calculate end of dispute window
    const lastUpdated = new Date(market?.latestResolution?.reviewed_at);
    const disputeWindowEnd = new Date(
      lastUpdated.getTime() + resolutionWindowHours * 60 * 60 * 1000
    );

    // Calculate remaining time
    const now = new Date();

    // If dispute window has passed, return null or expired message
    if (now > disputeWindowEnd) {
      return "Dispute window expired";
    }

    const remainingMs = disputeWindowEnd - now;
    const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
    const remainingMinutes = Math.floor(
      (remainingMs % (1000 * 60 * 60)) / (1000 * 60)
    );

    return `Dispute window: ${remainingHours}h ${remainingMinutes}m remaining`;
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Event Image</span>,
      cell: (row) => (
        <div style={{ padding: "5px" }}>
          {row.event_id?.event_image ? (
            <img
              src={row.event_id.event_image}
              alt={row.event_id.event_title}
              style={{
                width: "40px",
                height: "40px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
          ) : (
            <div
              style={{
                width: "40px",
                height: "40px",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                fontSize: "10px",
              }}
            >
              No Image
            </div>
          )}
        </div>
      ),
      width: "80px",
    },
    {
      name: <span className="font-weight-bold fs-13">Event Title</span>,
      cell: (row) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "100%",
          }}
        >
          <div>{row.event_id?.event_title || "N/A"}</div>
          <div className="small text-muted">{row.name}</div>
        </div>
      ),
      sortable: true,
      grow: 2,
    },
    {
      name: <span className="font-weight-bold fs-13">Status</span>,
      cell: (row) => {
        const statusText = getResolutionStatusText(row.resolution_state);
        const badgeClass = getStatusBadgeColor(row.resolution_state);
        const disputeWindowRemaining = getDisputeWindowRemaining(row);

        return (
          <div>
            <span className={`badge ${badgeClass}`}>{statusText}</span>
            {disputeWindowRemaining && (
              <div className="mt-1">
                <small className="text-muted">{disputeWindowRemaining}</small>
              </div>
            )}
          </div>
        );
      },
      sortable: true,
      width: "180px",
    },
    {
      name: <span className="font-weight-bold fs-13">Evidence</span>,
      cell: (row) => {
        if (!row.latestResolution || !row.latestResolution.evidence_url) {
          return <span className="text-muted">No evidence provided</span>;
        }

        return (
          <div className="evidence-details">
            <div>
              <a
                href={row.latestResolution.evidence_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-soft-primary"
              >
                <i className="ri-external-link-line me-1"></i>
                View Evidence
              </a>
            </div>
            {row.latestResolution.evidence_description && (
              <div
                className="mt-2 text-muted"
                style={{
                  maxHeight: "60px",
                  overflow: "auto",
                  fontSize: "12px",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                }}
              >
                {row.latestResolution.evidence_description}
              </div>
            )}
          </div>
        );
      },
      width: "200px",
    },
    {
      name: <span className="font-weight-bold fs-13">Latest Resolution</span>,
      cell: (row) => {
        if (!row.latestResolution) return "None";

        return (
          <div className="resolution-details">
            <div>
              <span
                className={`badge ${
                  row.latestResolution.status === "approved"
                    ? "bg-success"
                    : row.latestResolution.status === "pending"
                    ? "bg-warning"
                    : "bg-secondary"
                }`}
              >
                {row.latestResolution.status.charAt(0).toUpperCase() +
                  row.latestResolution.status.slice(1)}
              </span>
              <span className="ms-2">
                {row.latestResolution.resolution_type === "dispute" && (
                  <span>
                    Round: <strong>{row.latestResolution.dispute_round}</strong>
                  </span>
                )}
              </span>
              <div>
                Result: <strong>{row.latestResolution.proposed_result}</strong>
              </div>
            </div>

            <div className="mt-1 small text-muted">
              By: {row.latestResolution.user_id?.username || "Unknown"}
            </div>
          </div>
        );
      },
      grow: 3,
    },
    {
      name: <span className="font-weight-bold fs-13">Dispute Count</span>,
      cell: (row) => row.dispute_count || 0,
      sortable: true,
      width: "120px",
    },
    {
      name: <span className="font-weight-bold fs-13">Actions</span>,
      cell: (row) => {
        const isProcessing =
          processingResolution === (row.latestResolution?._id || "");

        if (row.latestResolution && row.latestResolution.status === "pending") {
          return (
            <UncontrolledDropdown direction="start">
              <DropdownToggle
                tag="button"
                className="btn btn-sm btn-soft-primary"
              >
                {isProcessing ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-more-fill align-middle"></i>
                )}
              </DropdownToggle>
              <DropdownMenu
                className="dropdown-menu-end"
                style={{
                  minWidth: "150px",
                  transform: "none",
                  top: "auto",
                  left: "auto",
                  bottom: "auto",
                  right: "auto",
                }}
              >
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                    openConfirmationModal(
                      row.latestResolution._id,
                      "approved",
                      {
                        marketName: row.name,
                        proposedResult: row.latestResolution.proposed_result,
                        username:
                          row.latestResolution.user_id?.username || "Unknown",
                      }
                    );
                  }}
                  disabled={isProcessing}
                >
                  <i className="ri-check-line align-bottom me-2 text-success"></i>{" "}
                  Approve
                </DropdownItem>
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                    openConfirmationModal(
                      row.latestResolution._id,
                      "rejected",
                      {
                        marketName: row.name,
                        proposedResult: row.latestResolution.proposed_result,
                        username:
                          row.latestResolution.user_id?.username || "Unknown",
                      }
                    );
                  }}
                  disabled={isProcessing}
                >
                  <i className="ri-close-line align-bottom me-2 text-danger"></i>{" "}
                  Reject
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        } else {
          return <span className="text-muted">No Actions</span>;
        }
      },
      width: "120px",
      sortable: false,
      right: true,
    },
  ];

  return (
    <>
      <Card>
        <CardBody>
          <Form>
            <Row className="mb-3">
              {/* <Col lg="4">
                <Input
                  type="search"
                  placeholder="Search events..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setPagination({...pagination, page: 1}); // Reset to first page on new search
                  }}
                />
              </Col>
              <Col lg="3">
                <Input
                  type="select"
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setPagination({...pagination, page: 1}); // Reset to first page on new filter
                  }}
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </Input>
              </Col> */}
            </Row>
          </Form>

          <DataTable
            columns={columns}
            data={markets}
            pagination
            paginationServer
            paginationTotalRows={pagination.totalRows}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            progressPending={loading}
            responsive
            highlightOnHover
            pointerOnHover
            onRowClicked={handleRowClick}
          />
        </CardBody>
      </Card>

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmationModal.isOpen}
        toggle={closeConfirmationModal}
        centered
      >
        <ModalHeader toggle={closeConfirmationModal}>
          {confirmationModal.action === "approved"
            ? "Approve Resolution"
            : "Reject Resolution"}
        </ModalHeader>
        <ModalBody>
          {confirmationModal.resolutionInfo && (
            <div>
              <p>
                Are you sure you want to{" "}
                {confirmationModal.action === "approved" ? "approve" : "reject"}{" "}
                the resolution for:
              </p>
              <p>
                <strong>Market:</strong>{" "}
                {confirmationModal.resolutionInfo.marketName}
              </p>
              <p>
                <strong>Proposed Result:</strong>{" "}
                {confirmationModal.resolutionInfo.proposedResult}
              </p>
              <p>
                <strong>Proposed By:</strong>{" "}
                {confirmationModal.resolutionInfo.username}
              </p>
              <p className="text-muted">
                {confirmationModal.action === "approved"
                  ? "Approving this resolution will confirm the proposed outcome."
                  : "Rejecting this resolution will deny the proposed outcome."}
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeConfirmationModal}>
            Cancel
          </Button>
          <Button
            color={
              confirmationModal.action === "approved" ? "success" : "danger"
            }
            onClick={confirmAction}
            disabled={processingResolution !== null}
          >
            {processingResolution !== null && (
              <Spinner size="sm" className="me-1" />
            )}
            {confirmationModal.action === "approved" ? "Approve" : "Reject"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ActiveResolutionList;
